import React, { useState, useEffect } from 'react';
import packageService from '../../../services/packageService';
import ProfileTestPreviewModal from './ProfileTestPreviewModal';

export const PackageDetailDrawer = ({
  packageItem,
  onClose,
  onEdit,
  onOpenPanelMapping
}) => {
  const [components, setComponents] = useState([]);
  const [resolvedTests, setResolvedTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewProfile, setPreviewProfile] = useState(null);
  const [activeViewTab, setActiveViewTab] = useState('COMPONENTS'); // 'COMPONENTS' | 'RESOLVED'

  useEffect(() => {
    if (!packageItem?.packageId) return;

    let isMounted = true;
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const [compRes, resRes] = await Promise.all([
          packageService.getPackageComponents(packageItem.packageId),
          packageService.getResolvedTests(packageItem.packageId)
        ]);

        if (isMounted) {
          setComponents(compRes && compRes.success ? (compRes.data || []) : []);
          setResolvedTests(resRes && resRes.success ? (resRes.data || []) : []);
        }
      } catch (err) {
        console.error('Error loading package details:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDetails();
    return () => {
      isMounted = false;
    };
  }, [packageItem]);

  if (!packageItem) return null;

  const profiles = components.filter(c => c.componentType === 'PROFILE');
  const directTests = components.filter(c => c.componentType === 'TEST');

  return (
    <div className="pkg-drawer-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pkg-drawer fade-in" onClick={(e) => e.stopPropagation()}>
        {/* Drawer Header */}
        <div className="pkg-drawer-header">
          <div>
            <div className="pkg-card-top-row">
              <span className="pkg-badge-package">HEALTH PACKAGE SPECIFICATION</span>
              <span className={`pkg-review-status-pill ${packageItem.isActive ? 'active' : 'inactive'}`}>
                {packageItem.isActive ? '● Active' : '○ Inactive'}
              </span>
            </div>
            <h3 className="pkg-drawer-title">{packageItem.packageName}</h3>
            <span className="pkg-drawer-subtitle">
              Code: <strong>{packageItem.packageCode}</strong>
            </span>
          </div>

          <button type="button" className="pkg-modal-close" onClick={onClose} aria-label="Close drawer">
            ✕
          </button>
        </div>

        {/* Drawer Body */}
        <div className="pkg-drawer-body">
          {packageItem.description && (
            <div className="pkg-drawer-description-card">
              <span className="pkg-drawer-section-label">CLINICAL DESCRIPTION & PREPARATION</span>
              <p>{packageItem.description}</p>
            </div>
          )}

          {/* Quick Metrics */}
          <div className="pkg-drawer-stats-row">
            <div className="pkg-drawer-stat-pill">
              <span className="pkg-stat-num">{profiles.length}</span>
              <span className="pkg-stat-lbl">Profiles</span>
            </div>
            <div className="pkg-drawer-stat-pill">
              <span className="pkg-stat-num">{directTests.length}</span>
              <span className="pkg-stat-lbl">Direct Tests</span>
            </div>
            <div className="pkg-drawer-stat-pill highlight">
              <span className="pkg-stat-num">{resolvedTests.length || (packageItem.testCount || 0)}</span>
              <span className="pkg-stat-lbl">Total Tests</span>
            </div>
          </div>

          {/* Toggle View: Components Breakdown vs All Resolved Tests */}
          <div className="pkg-drawer-tabs">
            <button
              type="button"
              className={`pkg-drawer-tab ${activeViewTab === 'COMPONENTS' ? 'active' : ''}`}
              onClick={() => setActiveViewTab('COMPONENTS')}
            >
              Composition Breakdown ({components.length})
            </button>
            <button
              type="button"
              className={`pkg-drawer-tab ${activeViewTab === 'RESOLVED' ? 'active' : ''}`}
              onClick={() => setActiveViewTab('RESOLVED')}
            >
              All Resolved Tests ({resolvedTests.length})
            </button>
          </div>

          {loading ? (
            <div className="pkg-loading-state">
              <div className="pkg-spinner"></div>
              <span>Fetching complete package clinical structure...</span>
            </div>
          ) : activeViewTab === 'COMPONENTS' ? (
            <div className="pkg-drawer-content-flow">
              {/* Profiles Section */}
              <div className="pkg-drawer-group">
                <div className="pkg-drawer-group-title">
                  <span className="pkg-section-icon">📋</span>
                  <h5>Clinical Profiles ({profiles.length})</h5>
                </div>

                {profiles.length === 0 ? (
                  <div className="pkg-review-empty-box">No profiles inside this package.</div>
                ) : (
                  <div className="pkg-drawer-items-list">
                    {profiles.map((p, idx) => (
                      <div key={p.id || idx} className="pkg-drawer-item profile">
                        <div className="pkg-drawer-item-icon">📋</div>
                        <div className="pkg-drawer-item-info">
                          <span className="pkg-drawer-item-name">{p.componentName}</span>
                          <span className="pkg-drawer-item-code">
                            Code: {p.componentCode || '—'} {p.departmentName ? `• ${p.departmentName}` : ''}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="pkg-btn-secondary-xs"
                          onClick={() => setPreviewProfile({
                            profileId: p.componentId,
                            profileName: p.componentName,
                            profileCode: p.componentCode
                          })}
                        >
                          👁️ View Tests
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Direct Tests Section */}
              <div className="pkg-drawer-group">
                <div className="pkg-drawer-group-title">
                  <span className="pkg-section-icon">🧪</span>
                  <h5>Direct Individual Tests ({directTests.length})</h5>
                </div>

                {directTests.length === 0 ? (
                  <div className="pkg-review-empty-box">No direct tests inside this package.</div>
                ) : (
                  <div className="pkg-drawer-items-list">
                    {directTests.map((t, idx) => (
                      <div key={t.id || idx} className="pkg-drawer-item test">
                        <div className="pkg-drawer-item-icon">🧪</div>
                        <div className="pkg-drawer-item-info">
                          <span className="pkg-drawer-item-name">{t.componentName}</span>
                          <span className="pkg-drawer-item-code">
                            Code: {t.componentCode || '—'} • {t.departmentName || 'General Pathology'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Resolved Tests Tab */
            <div className="pkg-drawer-resolved-list">
              <span className="pkg-resolved-hint">
                Every patient registration with this package will automatically spawn lab orders for these {resolvedTests.length} tests:
              </span>
              <div className="pkg-resolved-grid">
                {resolvedTests.map((rt, idx) => (
                  <div key={idx} className="pkg-resolved-chip">
                    <span className="pkg-resolved-check">✓</span>
                    <div className="pkg-resolved-chip-content">
                      <span className="pkg-resolved-test-name">{rt.investigationName || rt.name}</span>
                      <span className="pkg-resolved-source">
                        {rt.source === 'PROFILE' ? `via Profile: ${rt.profileName}` : 'Direct standalone test'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="pkg-drawer-footer">
          <div className="pkg-drawer-footer-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                onClose();
                onOpenPanelMapping(packageItem);
              }}
            >
              🏛️ Panel Tariffs & Pricing
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                onClose();
                onEdit(packageItem);
              }}
            >
              ✏️ Edit Package & Builder
            </button>
          </div>
        </div>
      </div>

      {previewProfile && (
        <ProfileTestPreviewModal
          profile={previewProfile}
          onClose={() => setPreviewProfile(null)}
        />
      )}
    </div>
  );
};

export default PackageDetailDrawer;
